<!-- SmcChat.vue -->
<template>
  <!-- Floating button -->
  <button
    class="smc-chat-fab"
    :aria-expanded="isOpen"
    aria-label="채팅 열기"
    title="채팅"
    @click="isOpen = !isOpen"
  >
    💬
  </button>

  <!-- Floating panel -->
  <transition name="smc-fade">
    <div v-if="isOpen" class="smc-chat-panel" role="dialog" aria-modal="true">
      <!-- Header -->
      <div class="smc-chat-header">
        <div class="smc-title">
          <!-- ▼ 메타(⟦meta⟧)로 받은 세션 제목만 표시. 없으면 '새 대화' -->
          <strong>
            {{
              currentSessionTitle && currentSessionTitle.length > 0
                ? currentSessionTitle
                : $t("ai.text.new_chat")
            }}
          </strong>
          <small class="smc-session-key">
            {{ $t("ai.text.bot_name") }}
          </small>
        </div>

        <!-- 🔊 말하는 중 뱃지 + 중지 -->
        <div v-if="isStreaming" class="smc-speaking-badge" aria-live="polite">
          <span class="eq"><i></i><i></i><i></i><i></i></span>
          <span>{{ $t("ai.text.speaking") || "말하는 중…" }}</span>
        </div>
        <button
          v-if="isStreaming"
          class="smc-btn danger ghost smc-stop-btn"
          @click="stopStreaming"
        >
          ■
        </button>

        <div class="smc-actions">
          <button class="smc-btn ghost" @click="toggleView">
            {{
              viewMode === "chat"
                ? $t("ai.button.chat_list")
                : $t("ai.button.chatting")
            }}
          </button>
          <button class="smc-btn ghost" @click="isOpen = false">✕</button>
        </div>
      </div>

      <!-- Body -->
      <div class="smc-chat-body">
        <!-- Sessions list -->
        <div v-if="viewMode === 'sessions'" class="smc-sessions">
          <div class="smc-session-toolbar">
            <button
              class="smc-btn"
              :disabled="creating"
              @click="createNewSession"
            >
              {{ $t("ai.button.start_a_new_chat") }}
            </button>
            <div class="smc-spacer"></div>
            <div class="smc-pager">
              <button
                class="smc-btn ghost"
                :disabled="pageNo === 0 || loadingSessions"
                @click="prevPage"
              >
                {{ $t("ai.button.prev") }}
              </button>
              <span class="smc-page">p. {{ pageNo + 1 }}</span>
              <button
                class="smc-btn ghost"
                :disabled="!hasMore || loadingSessions"
                @click="nextPage"
              >
                {{ $t("ai.button.next") }}
              </button>
            </div>
          </div>

          <div class="smc-session-list" :aria-busy="loadingSessions">
            <div
              v-for="s in sessions"
              :key="s.id ?? s.sessionKey"
              class="smc-session-item smc-session-row"
              :class="{
                active: s.sessionKey === currentSessionKey,
                open: openMenuKey === s.sessionKey,
              }"
              @click="openSession(s.sessionKey, s.title)"
              @mouseenter="hoverSessionKey = s.sessionKey"
              @mouseleave="hoverSessionKey = null"
            >
              <div class="smc-session-main">
                <div class="smc-session-title">
                  {{ s.title || "(" + $t("ai.text.no_title") + ")" }}
                </div>
                <div class="smc-session-meta">
                  Session key : {{ s.sessionKey }}
                </div>
              </div>

              <!-- Kebab -->
              <button
                v-show="hoverSessionKey === s.sessionKey"
                class="smc-kebab"
                :aria-expanded="openMenuKey === s.sessionKey"
                :aria-haspopup="true"
                :title="$t('ai.button.more_options')"
                @click.stop="toggleSessionMenu(s.sessionKey)"
              >
                ⋮
              </button>

              <!-- Dropdown -->
              <div
                v-if="openMenuKey === s.sessionKey"
                class="smc-menu"
                @click.stop
              >
                <button
                  class="smc-menu-item danger"
                  @click="confirmDeleteSession(s.sessionKey)"
                >
                  {{ $t("ai.button.delete") }}
                </button>
              </div>
            </div>

            <div
              v-if="!loadingSessions && sessions.length === 0"
              class="smc-empty"
            >
              {{ $t("ai.guide.no_chattings") }}
              <button class="smc-link" @click="createNewSession">
                {{ $t("ai.guide.start_chatting") }}
              </button>
            </div>
          </div>
        </div>

        <!-- Chat -->
        <div v-else class="smc-chat">
          <div
            ref="messageListRef"
            class="smc-messages"
            :aria-busy="sending || loadingRecent || loadingOlder || isStreaming"
            @scroll.passive="onMessageScroll"
          >
            <div v-if="loadingOlder" class="smc-top-loader">
              {{ $t("ai.text.loading_more") || "Loading more…" }}
            </div>

            <template v-if="messages.length">
              <div
                v-for="(m, idx) in messages"
                :key="m.id ?? idx"
                class="smc-msg"
                :class="m.role"
              >
                <div class="bubble">
                  <div class="content">
                    <!-- typing dots -->
                    <span v-if="m.typing" class="smc-typing"
                      ><i class="dot"></i><i class="dot"></i><i class="dot"></i
                    ></span>

                    <!-- 유저: 일반 텍스트 / 어시스턴트: 마크다운 HTML -->
                    <template v-else>
                      <span v-if="m.role === 'user'">{{ m.text }}</span>
                      <div v-else v-html="renderMarkdown(m.text)"></div>
                    </template>
                  </div>
                  <div class="meta">
                    <span
                      v-if="!m.typing && m.source && m.source !== 'user'"
                      class="source"
                      >{{ m.source }}</span
                    >
                    <span class="time">{{ m.time }}</span>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="smc-empty">
              {{
                currentSessionKey
                  ? $t("ai.guide.start_chatting")
                  : $t("ai.guide.what_can_i_do_for_you")
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- Footer / input -->
      <div v-if="viewMode === 'chat'" class="smc-chat-input">
        <textarea
          ref="textAreaRef"
          v-model="draft"
          class="smc-textarea"
          rows="2"
          :placeholder="$t('ai.placeholder.input')"
          :disabled="sending"
          @input="onInput"
          @keydown="onEnter"
          @keydown.esc.prevent="isStreaming && stopStreaming()"
          @compositionstart="onCompStart"
          @compositionupdate="onCompUpdate"
          @compositionend="onCompEnd"
        />

        <!-- 스트리밍 중: 중지 버튼 / 아니면 보내기 -->
        <button
          v-if="isStreaming"
          class="smc-btn danger"
          @click="stopStreaming"
        >
          ■
        </button>
        <button
          v-else
          class="smc-btn primary"
          :disabled="sending || !canSend"
          @click="send"
        >
          {{ $t("ai.button.send") }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  nextTick,
  watch,
  onBeforeUnmount,
} from "vue";
import { useI18n } from "vue-i18n";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
import { makeToast } from "~/composables/useToast.ts";
import { useModalStore } from "~/stores/modal.js";

const { t } = useI18n();

/* ── Markdown 렌더러 (줄바꿈 유지, 자동 링크, 안전화) ── */
const md = new MarkdownIt({ breaks: true, linkify: true, html: false });
const defaultLinkOpen =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const ti = tokens[idx].attrIndex("target");
  if (ti < 0) {
    tokens[idx].attrPush(["target", "_blank"]);
  } else {
    tokens[idx].attrs[ti][1] = "_blank";
  }
  const ri = tokens[idx].attrIndex("rel");
  if (ri < 0) {
    tokens[idx].attrPush(["rel", "noopener noreferrer"]);
  } else {
    tokens[idx].attrs[ri][1] = "noopener noreferrer";
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};
function renderMarkdown(text = "") {
  const cleaned = String(text).replace(/\s+$/, "");
  return DOMPurify.sanitize(md.render(cleaned));
}

// --- UI state ---
const isOpen = ref(false);
const viewMode = ref("chat");
const draft = ref("");
const textAreaRef = ref(null);

// IME 조합 상태 + 전송 가능 여부
const isComposing = ref(false);
const composingValue = ref("");
const canSend = computed(() => {
  const raw = isComposing.value ? composingValue.value : draft.value;
  return !!raw && raw.trim().length > 0;
});

// --- sessions state ---
const sessions = ref([]);
const pageNo = ref(0);
const size = ref(10);
const hasMore = ref(false);
const loadingSessions = ref(false);
const creating = ref(false);

// 스트리밍 제어
const isStreaming = ref(false);
const streamAbort = ref(null); // AbortController
const streamReader = ref(null); // ReadableStreamDefaultReader

// kebab hover & menu
const hoverSessionKey = ref(null);
const openMenuKey = ref(null);

// --- current chat state ---
const currentSessionKey = ref(null);
const currentSessionTitle = ref(null);
const messages = ref([]); // { id, role, text, source?, time, typing? }
const sending = ref(false);
const loadingRecent = ref(false);
const loadingOlder = ref(false);
const noMoreOlder = ref(false);
const modalStore = useModalStore();
const messageListRef = ref(null);

// 메타 프리픽스(백엔드와 합의된 토큰)
const META_PREFIX = "⟦meta⟧";

// Toast helper
function toast(msg) {
  try {
    makeToast(msg);
  } catch {
    console.log(msg);
  }
}

/* ===== 입력/조합 핸들러 ===== */
function onInput(e) {
  if (isComposing.value) {
    composingValue.value = e.target.value ?? "";
  }
}
function onCompStart(e) {
  isComposing.value = true;
  composingValue.value = e.target?.value ?? "";
}
function onCompUpdate(e) {
  composingValue.value = e.target?.value ?? composingValue.value;
}
function onCompEnd() {
  isComposing.value = false;
  composingValue.value = "";
}

/* ===== 토큰 보정 유틸 ===== */
function normalizeToken(token) {
  if (!token) {
    return "";
  }
  token = token.replace(/\r\n/g, "\n");
  token = token.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
  token = token.replace(/\u0000/g, "");
  return token;
}

function appendToken(prev, rawToken) {
  const token = normalizeToken(rawToken);
  if (!token) {
    return prev;
  }
  if (token.includes("\n")) {
    return prev + token;
  }
  if (/^\s/.test(token)) {
    return prev + token;
  }

  const noSpaceBefore = /^[\)\]\}\.,:;!?%'"`’”〉》»]+$/;
  const noSpaceAfterLast = /[\(\[\{£$#@“"‘'`〈《«]+$/;
  const needNoSpace =
    noSpaceBefore.test(token) ||
    noSpaceAfterLast.test(prev) ||
    prev.endsWith("\n") ||
    prev.length === 0;

  return needNoSpace ? prev + token : prev + " " + token;
}

/** 세션 제목을 UI 상태와 세션 목록에 반영 */
function applySessionTitle(newTitle) {
  if (!newTitle) {
    return;
  }
  currentSessionTitle.value = newTitle;

  if (Array.isArray(sessions.value) && currentSessionKey.value) {
    const idx = sessions.value.findIndex(
      (s) => s.sessionKey === currentSessionKey.value,
    );
    if (idx >= 0) {
      sessions.value[idx] = { ...sessions.value[idx], title: newTitle };
    }
  }
}

/* --- 스트리밍 중지 --- */
async function stopStreaming() {
  try {
    if (streamReader.value && typeof streamReader.value.cancel === "function") {
      await streamReader.value.cancel();
    }
  } catch {}
  try {
    if (streamAbort.value && typeof streamAbort.value.abort === "function") {
      streamAbort.value.abort();
    }
  } catch {}
  isStreaming.value = false;

  await nextTick();
  if (textAreaRef.value) {
    textAreaRef.value.focus();
  }
}

/* --- 스트리밍 파서 --- */
async function streamChat(userText) {
  isStreaming.value = true;

  // AbortController 생성
  const controller = new AbortController();
  streamAbort.value = controller;

  // 메타 플래그(옵션): 백엔드가 titleChanged 힌트를 보낼 때 사용
  let titleMayChangeLater = false;

  try {
    const res = await customFetch("/ai/tc/chat/stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "text/event-stream",
      },
      body: JSON.stringify({
        sessionKey: currentSessionKey.value,
        message: userText,
        stream: true,
      }),
      hideLoading: true,
      useFetch: true,
      signal: controller.signal,
    });

    if (!res || !res.body || typeof res.body.getReader !== "function") {
      throw new Error("Stream not available");
    }

    const reader = res.body.getReader();
    streamReader.value = reader;

    const decoder = new TextDecoder("utf-8");
    let done = false;
    let buffer = "";
    let seenOk = false;
    let gotFirstToken = false;

    // 어시스턴트 버블 자리
    if (
      !messages.value.length ||
      messages.value[messages.value.length - 1].role !== "assistant"
    ) {
      messages.value.push({
        id: undefined,
        role: "assistant",
        typing: true,
        text: "",
        source: null,
        time: nowStr(),
      });
      await nextTick();
      await scrollToBottom();
    }
    const last = () => messages.value[messages.value.length - 1];

    while (!done) {
      const { value, done: streamDone } = await reader.read();
      done = streamDone;

      if (value) {
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? "";

        for (const raw of lines) {
          if (!raw) {
            continue;
          }

          // SSE comment
          if (raw.startsWith(":")) {
            if (raw.trim() === ":ok") {
              seenOk = true;
            }
            continue;
          }

          // data: prefix 제거
          let data = raw;
          while (data.startsWith("data:")) {
            data = data.slice(5);
          }
          if (data.startsWith(" ")) {
            data = data.slice(1);
          }
          if (!data) {
            continue;
          }

          // 안전: :ok가 data로 올 수도
          if (!seenOk && (data === ":ok" || data === "::ok" || data === "ok")) {
            seenOk = true;
            continue;
          }

          // 종료
          if (data === "[DONE]" || data === "<|eot|>") {
            done = true;
            break;
          }

          // ⟦meta⟧{...} 첫 메타 청크 처리 (제목 갱신)
          if (data.startsWith(META_PREFIX)) {
            const metaStr = data.slice(META_PREFIX.length);
            try {
              console.log(metaStr.trim());
              const meta = JSON.parse(metaStr.trim());
              if (
                meta &&
                typeof meta.title === "string" &&
                meta.title.length > 0
              ) {
                applySessionTitle(meta.title);
              }
              if (meta && meta.titleChanged === true) {
                titleMayChangeLater = true;
              }
            } catch (e) {
              console.warn("meta parse failed:", e);
            }
            continue; // 메타는 말풍선에 넣지 않음
          }

          if (!seenOk) {
            continue;
          }

          const L = last();
          if (L.typing && !gotFirstToken) {
            L.typing = false;
            gotFirstToken = true;
          }

          L.text = appendToken(L.text, data);

          await nextTick();
          await scrollToBottom();
        }
      }
    }

    const L = last();
    if (L) {
      L.typing = false;
      L.time = nowStr();
    }
  } catch (err) {
    // 사용자가 중지한 경우도 여기로 들어옴
    console.log("stream error/abort:", err?.name || err);
    const L = messages.value[messages.value.length - 1];
    if (L?.role === "assistant" && !L.text) {
      messages.value.pop();
    }
  } finally {
    isStreaming.value = false;
    streamAbort.value = null;
    streamReader.value = null;
    await nextTick();
    if (textAreaRef.value) {
      textAreaRef.value.focus();
    }

    // (옵션) 이번 턴 중 제목이 바뀔 수 있음을 메타가 시사하면, 세션 목록 새로고침
    if (titleMayChangeLater === true && viewMode.value === "sessions") {
      try {
        await loadSessions();
      } catch {}
    }
  }
}

// 시간 포맷
function nowStr() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// 스크롤 바닥으로
async function scrollToBottom() {
  await nextTick();
  const el = messageListRef.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
}

// 상단 스크롤 감지 → 이전 메시지 로드
function onMessageScroll(e) {
  const el = e.target;
  if (!el || loadingOlder.value || !currentSessionKey.value) {
    return;
  }
  if (noMoreOlder.value) {
    return;
  }
  if (el.scrollTop <= 10) {
    loadOlderMessages();
  }
}

function getMinMessageId() {
  if (!messages.value.length) {
    return null;
  }
  const ids = messages.value.map((m) => Number(m.id)).filter(Number.isFinite);
  if (!ids.length) {
    return null;
  }
  return Math.min(...ids);
}

// 이전 메시지 호출 & prepend, 스크롤 보정
async function loadOlderMessages() {
  const lastLoadedId = getMinMessageId();
  if (!lastLoadedId) {
    return;
  }

  loadingOlder.value = true;
  const el = messageListRef.value;
  const prevScrollHeight = el ? el.scrollHeight : 0;

  try {
    const url = `/ai/chatMessages?sessionKey=${encodeURIComponent(
      currentSessionKey.value,
    )}&lastLoadedId=${encodeURIComponent(lastLoadedId)}`;
    const res = await customFetch(url, { method: "GET", hideLoading: true });

    if (res?.resultCode === "success" && Array.isArray(res.data)) {
      if (res.data.length === 0) {
        noMoreOlder.value = true;
        return;
      }

      const older = res.data.map((r) => ({
        id: r.id ?? undefined,
        role: r.role,
        text: r.content ?? "",
        source: r.source ?? null,
        time: nowStr(),
      }));

      messages.value = [...older, ...messages.value];

      await nextTick();
      if (el) {
        const added = el.scrollHeight - prevScrollHeight;
        el.scrollTop = added;
      }
    }
  } catch (e) {
    console.error(e);
    toast(t("ai.error.load_older"));
  } finally {
    loadingOlder.value = false;
  }
}

// 세션 목록 로드
async function loadSessions() {
  loadingSessions.value = true;
  try {
    const url = `/ai/sessions?pageNo=${encodeURIComponent(pageNo.value)}&size=${encodeURIComponent(size.value)}`;
    const res = await customFetch(url, { method: "GET" });

    if (res?.resultCode === "success" && Array.isArray(res.data?.content)) {
      sessions.value = res.data.content;
      hasMore.value =
        Number(res.data?.numberOfElements ?? 0) === Number(size.value);
    } else {
      sessions.value = [];
      hasMore.value = false;
      toast(t("ai.error.load_sessions"));
    }
  } catch (e) {
    console.error(e);
    sessions.value = [];
    hasMore.value = false;
    toast(t("ai.error.load_sessions2"));
  } finally {
    loadingSessions.value = false;
  }
}

function goToPage(n) {
  if (n < 0 || loadingSessions.value) {
    return;
  }
  pageNo.value = n;
  loadSessions();
}
function nextPage() {
  if (!hasMore.value || loadingSessions.value) {
    return;
  }
  goToPage(pageNo.value + 1);
}
function prevPage() {
  if (pageNo.value === 0 || loadingSessions.value) {
    return;
  }
  goToPage(pageNo.value - 1);
}

function toggleView() {
  viewMode.value = viewMode.value === "chat" ? "sessions" : "chat";
  if (viewMode.value === "sessions") {
    pageNo.value = 0;
    loadSessions();
  }
}

// 새 세션 생성
async function createNewSession() {
  creating.value = true;
  try {
    const res = await customFetch("/ai/session", { method: "POST" });
    if (res?.resultCode === "success" && res?.data?.sessionKey) {
      currentSessionKey.value = res.data.sessionKey;
      currentSessionTitle.value = ""; // 메타 도착 전까지는 표시 안 함
      messages.value = [];
      noMoreOlder.value = false;
      viewMode.value = "chat";
      toast(t("ai.toast.new_session"));
      await nextTick();
      if (textAreaRef.value) {
        textAreaRef.value.focus();
      }
    } else {
      toast(t("ai.error.create_sessions"));
    }
  } catch (e) {
    console.error(e);
    toast(t("ai.error.create_sessions2"));
  } finally {
    creating.value = false;
  }
}

// 특정 세션 열기
async function openSession(sessionKey, sessionTitle) {
  currentSessionKey.value = sessionKey;
  // 서버에 저장된 제목을 그대로 사용(없으면 빈 값으로 두고 메타를 기다림)
  currentSessionTitle.value =
    typeof sessionTitle === "string" ? sessionTitle : "";
  viewMode.value = "chat";
  openMenuKey.value = null;
  noMoreOlder.value = false;
  await loadRecentChats(sessionKey);
}

async function loadRecentChats(sessionKey) {
  loadingRecent.value = true;
  messages.value = [];
  noMoreOlder.value = false;

  try {
    const url = `/ai/chatMessages?sessionKey=${encodeURIComponent(sessionKey)}`;
    const res = await customFetch(url, { method: "GET" });

    if (res?.resultCode === "success" && Array.isArray(res.data)) {
      messages.value = res.data.map((r) => ({
        id: r.id ?? undefined,
        role: r.role,
        text: r.content ?? "",
        source: r.source ?? null,
        time: nowStr(),
      }));
      await scrollToBottom();
      if (messages.value.length === 0) {
        noMoreOlder.value = true;
      }
    } else {
      toast(t("ai.error.load_recent"));
    }
  } catch (e) {
    console.error(e);
  } finally {
    loadingRecent.value = false;
  }
}

// 메시지 전송 -> 스트리밍으로 전환
async function send() {
  const el = textAreaRef.value;
  if (!el) {
    return;
  }

  const text = isComposing.value ? composingValue.value : el.value;
  if (!text || sending.value || isStreaming.value) {
    return;
  }

  // 세션 없으면 생성
  if (!currentSessionKey.value) {
    await createNewSession();
    if (!currentSessionKey.value) {
      return;
    }
  }

  // ▼ 임시 제목 제거! (첫 문장으로 제목이 찍히던 원인)
  //    메타(⟦meta⟧{"title":...})가 오면 applySessionTitle()이 헤더를 갱신합니다.

  // 유저 말풍선
  messages.value.push({
    id: undefined,
    role: "user",
    source: "user",
    text,
    time: nowStr(),
  });

  // 입력 초기화 + 포커스 유지
  draft.value = "";
  composingValue.value = "";
  await nextTick();
  el.focus();
  await scrollToBottom();

  // 스트리밍 시작
  await streamChat(text);
}

function onEnter(e) {
  e.stopPropagation();
  if (e.shiftKey || isComposing.value || isStreaming.value || sending.value) {
    return;
  }
  if (e.keyCode === 13) {
    e.preventDefault();
    send();
  }
}

/* ===== 세션 삭제 드롭다운 ===== */
function toggleSessionMenu(sessionKey) {
  openMenuKey.value = openMenuKey.value === sessionKey ? null : sessionKey;
}
function confirmDeleteSession(sessionKey) {
  modalStore.openModal({
    content: t("ai.confirm.delete"),
    onOk: async () => {
      try {
        const url = `/ai/deleteSession?sessionKey=${encodeURIComponent(
          sessionKey,
        )}`;
        const res = await customFetch(url, { method: "GET" });
        if (res?.resultCode === "success") {
          sessions.value = sessions.value.filter(
            (s) => s.sessionKey !== sessionKey,
          );
          if (currentSessionKey.value === sessionKey) {
            currentSessionKey.value = null;
            currentSessionTitle.value = null;
            messages.value = [];
            noMoreOlder.value = true;
          }
          openMenuKey.value = null;
          toast(t("ai.toast.deleted"));
        }
      } catch (e) {
        console.error(e);
        toast(t("ai.error.delete_session2"));
      }
    },
  });
}

// 문서 클릭 시 외부 클릭으로 메뉴 닫기
function onDocClick(e) {
  const menu = document.querySelector(".smc-menu");
  const kebab = document.querySelector(".smc-kebab");
  if (
    openMenuKey.value &&
    menu &&
    !menu.contains(e.target) &&
    kebab &&
    !kebab.contains(e.target)
  ) {
    openMenuKey.value = null;
  }
}

// 초기 & 해제
onMounted(() => {
  document.addEventListener("click", onDocClick);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
});
watch(isOpen, async (v) => {
  if (v && viewMode.value === "chat") {
    await nextTick();
    if (textAreaRef.value) {
      textAreaRef.value.focus();
    }
  }
});
</script>

<style scoped>
/* === Theme tokens === */
html[data-coreui-theme="light"] .smc-chat-panel,
html[data-coreui-theme="light"] .smc-chat-panel *:not(.smc-msg .content) {
  --smc-bg: #ffffff;
  --smc-border: #e5e7eb;
  --smc-header-bg: #f8fafc;
  --smc-text: #111827;

  --smc-msg-bg-assist: #ffffff;
  --smc-msg-bg-assist-2: #f2f4f7;
  --smc-msg-fg-assist: #111827;
  --smc-msg-border-assist: rgba(17, 24, 39, 0.06);

  --smc-msg-bg-user-1: #3b82f6;
  --smc-msg-bg-user-2: #2563eb;
  --smc-msg-border-user: rgba(37, 99, 235, 0.75);
  --smc-msg-shadow-user: rgba(37, 99, 235, 0.25);
  --smc-msg-fg-user: #ffffff;

  --smc-surface: linear-gradient(180deg, #fbfbfd 0%, #f7f8fb 100%);

  --smc-btn-bg: #ffffff;
  --smc-btn-fg: #111827;
  --smc-btn-border: #e5e7eb;

  --smc-btn-primary-bg: #3b82f6;
  --smc-btn-primary-fg: #ffffff;
  --smc-btn-primary-border: #2563eb;

  --smc-input-bg: #ffffff;
  --smc-input-fg: #111827;
  --smc-input-border: #e5e7eb;
  --smc-link: #2563eb;
}
html[data-coreui-theme="dark"] .smc-chat-panel,
html[data-coreui-theme="dark"] .smc-chat-panel *:not(.smc-msg .content) {
  --smc-bg: #0b1220;
  --smc-border: #1f2a44;
  --smc-header-bg: #0f172a;
  --smc-text: #dbeafe;

  --smc-msg-bg-assist: #111827;
  --smc-msg-bg-assist-2: #0f172a;
  --smc-msg-fg-assist: #e5e7eb;
  --smc-msg-border-assist: rgba(148, 163, 184, 0.18);

  --smc-msg-bg-user-1: #2563eb;
  --smc-msg-bg-user-2: #1e40af;
  --smc-msg-border-user: rgba(59, 130, 246, 0.8);
  --smc-msg-shadow-user: rgba(59, 130, 246, 0.35);
  --smc-msg-fg-user: #ffffff;

  --smc-surface: linear-gradient(180deg, #0b1220 0%, #0e1527 100%);
  --smc-btn-bg: #0f172a;
  --smc-btn-fg: #e5e7eb !important;
  --smc-btn-border: #1f2a44;
  --smc-btn-primary-bg: #2563eb;
  --smc-btn-primary-fg: #ffffff !important;
  --smc-btn-primary-border: #1d4ed8;
  --smc-input-bg: #0f172a;
  --smc-input-fg: #e5e7eb;
  --smc-input-border: #1f2a44;
  --smc-link: #93c5fd;
}

/* Floating button */
.smc-chat-fab {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 9999;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #3b82f6;
  color: #fff !important;
  font-size: 22px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition:
    transform 0.1s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}
.smc-chat-fab:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
  background: #2563eb;
}
html[data-coreui-theme="dark"] .smc-chat-fab {
  background: #2563eb;
  color: #fff !important;
}

/* Panel */
.smc-chat-panel {
  position: fixed;
  right: 20px;
  bottom: 90px;
  width: 360px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  background: var(--smc-bg);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  z-index: 10000;
  border: 1px solid var(--smc-border);
}

/* Header */
.smc-chat-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: var(--smc-header-bg);
  border-bottom: 1px solid var(--smc-border);
  color: var(--smc-text);
}
.smc-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.smc-session-key {
  color: var(--smc-text);
  opacity: 0.7;
  font-size: 12px;
}

/* 🔊 말하는 중 뱃지 */
.smc-speaking-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 10px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  font-size: 12px;
  border: 1px solid rgba(59, 130, 246, 0.25);
}
html[data-coreui-theme="dark"] .smc-speaking-badge {
  background: rgba(147, 197, 253, 0.12);
  color: #93c5fd;
  border-color: rgba(147, 197, 253, 0.25);
}
.smc-speaking-badge .eq {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 10px;
}
.smc-speaking-badge .eq i {
  width: 2px;
  height: 6px;
  background: currentColor;
  display: inline-block;
  animation: smc-eq 1s infinite ease-in-out;
  border-radius: 1px;
}
.smc-speaking-badge .eq i:nth-child(1) {
  animation-delay: 0s;
}
.eq i:nth-child(2) {
  animation-delay: 0.15s;
}
.eq i:nth-child(3) {
  animation-delay: 0.3s;
}
.eq i:nth-child(4) {
  animation-delay: 0.45s;
}
@keyframes smc-eq {
  0%,
  100% {
    height: 4px;
    opacity: 0.6;
  }
  40% {
    height: 10px;
    opacity: 1;
  }
  60% {
    height: 7px;
    opacity: 0.8;
  }
}

.smc-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
}

/* Body */
.smc-chat-body {
  display: flex;
  min-height: 280px;
  max-height: 52vh;
  overflow: hidden;
  color: var(--smc-text);
}
.smc-chat {
  flex: 1;
  display: flex;
}
.smc-messages {
  flex: 1;
  padding: 14px 12px;
  overflow-y: auto;
  background: var(--smc-surface);
}

/* Top loader */
.smc-top-loader {
  text-align: center;
  font-size: 12px;
  opacity: 0.7;
  margin: 6px 0 8px;
}

/* Empty */
.smc-empty {
  padding: 24px;
  color: var(--smc-text);
  opacity: 0.7;
  text-align: center;
}

:root {
  --gap: 8px;
}
/* Message bubbles */
.smc-msg {
  display: flex;
  align-items: flex-end;
  gap: var(--gap);
  flex-wrap: nowrap;
  margin: 8px 0;
}
/* 여러 줄 코드블록 */
.smc-msg.assistant .content pre {
  white-space: pre-wrap;
  word-break: break-word;
  overflow: auto;
  max-width: 100%;
  box-sizing: border-box;
}

/* 인라인 코드도 너무 길면 줄바꿈 허용 */
.smc-msg.assistant .content code {
  white-space: pre-wrap;
  word-break: break-word;
}
/* 표가 넓을 때 가로 스크롤 */
.smc-msg.assistant .content table {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
}

/* 이미지/비디오가 버블 너비를 넘지 않도록 */
.smc-msg.assistant .content img,
.smc-msg.assistant .content video {
  max-width: 100%;
  height: auto;
  display: block;
}
.smc-msg .bubble {
  position: relative;
  max-width: 78%;
  padding: 10px 12px;
  border-radius: 16px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
  min-width: 0;
  box-sizing: border-box;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--smc-msg-border-assist);
  color: var(--smc-msg-fg-assist);
}
.smc-msg.assistant .bubble {
  background: linear-gradient(
    180deg,
    var(--smc-msg-bg-assist) 0%,
    var(--smc-msg-bg-assist-2) 100%
  );
  border-top-left-radius: 6px;
}
.smc-msg.assistant .bubble::after {
  content: "";
  position: absolute;
  left: -6px;
  bottom: 8px;
  width: 10px;
  height: 10px;
  background: inherit;
  border-left: 1px solid var(--smc-msg-border-assist);
  border-bottom: 1px solid var(--smc-msg-border-assist);
  transform: rotate(45deg);
  border-bottom-left-radius: 2px;
  box-shadow: -2px 2px 6px rgba(0, 0, 0, 0.04);
}
.smc-msg.user {
  justify-content: flex-end;
}
.smc-msg.user .bubble {
  background: linear-gradient(
    180deg,
    var(--smc-msg-bg-user-1) 0%,
    var(--smc-msg-bg-user-2) 100%
  );
  color: #fff;
  border-top-right-radius: 6px;
  border: 1px solid rgba(37, 99, 235, 0.75);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25);
}
.smc-msg.user .bubble::after {
  content: "";
  position: absolute;
  right: -6px;
  bottom: 8px;
  width: 10px;
  height: 10px;
  background: inherit;
  border-right: 1px solid rgba(37, 99, 235, 0.75);
  border-bottom: 1px solid rgba(37, 99, 235, 0.75);
  transform: rotate(-45deg);
  border-bottom-right-radius: 2px;
  box-shadow: 2px 2px 8px rgba(37, 99, 235, 0.25);
}

/* Content */
.smc-msg .content {
  margin: 0;
  font-family:
    system-ui,
    -apple-system,
    Segoe UI,
    Roboto,
    "Noto Sans KR",
    Arial,
    sans-serif;
  font-size: 14px;
  white-space: normal;
  overflow-wrap: anywhere;
}

/* Markdown typography (assistant only) */
.smc-msg.assistant .content h1,
.smc-msg.assistant .content h2,
.smc-msg.assistant .content h3 {
  margin: 6px 0 4px;
  font-weight: 700;
}
.smc-msg.assistant .content h1 {
  font-size: 16px;
}
.smc-msg.assistant .content h2 {
  font-size: 15px;
}
.smc-msg.assistant .content h3 {
  font-size: 14px;
}
.smc-msg.assistant .content p {
  margin: 4px 0;
}
.smc-msg.assistant .content ul,
.smc-msg.assistant .content ol {
  padding-left: 18px;
  margin: 4px 0;
}
.smc-msg.assistant .content li {
  margin: 2px 0;
}
.smc-msg.assistant .content a {
  text-decoration: underline;
}
.smc-msg.assistant .content code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  background: rgba(148, 163, 184, 0.15);
  padding: 0.1em 0.3em;
  border-radius: 4px;
}
.smc-msg.assistant .content pre {
  background: rgba(148, 163, 184, 0.15);
  padding: 8px;
  border-radius: 8px;
  overflow: auto;
  margin: 4px 0;
}

/* Meta line */
.smc-msg .meta {
  margin-top: 6px;
  display: flex;
  gap: 8px;
  font-size: 11px;
  opacity: 0.85;
}
.smc-msg.assistant .meta {
  color: #6b7280;
}
.smc-msg.user .meta {
  color: rgba(255, 255, 255, 0.9);
}
.smc-msg .meta .source {
  background: rgba(255, 255, 255, 0.5);
  color: inherit;
  border-radius: 8px;
  padding: 0 6px;
}
.smc-msg.user .meta .source {
  background: rgba(255, 255, 255, 0.18);
}

/* Typing dots (bubble-level) */
.smc-typing {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 28px;
}
.smc-typing .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.55;
  display: inline-block;
  animation: smc-typing-bounce 1.2s infinite ease-in-out;
}
.smc-typing .dot:nth-child(2) {
  animation-delay: 0.15s;
}
.smc-typing .dot:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes smc-typing-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.7);
    opacity: 0.35;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.smc-chat, .smc-messages, .smc-msg, .smc-msg .bubble, .smc-msg .content { min-width: 0; }

/* Input */
.smc-chat-input {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--smc-border);
  background: var(--smc-bg);
  color: var(--smc-text);
}
.smc-textarea {
  flex: 1;
  resize: none;
  padding: 10px;
  font-size: 14px;
  border-radius: 10px;
  background: var(--smc-input-bg);
  color: var(--smc-input-fg);
  border: 1px solid var(--smc-input-border);
  outline: none;
}
.smc-textarea::placeholder {
  color: rgba(148, 163, 184, 0.8);
}
.smc-textarea:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.smc-textarea:disabled {
  background: rgba(148, 163, 184, 0.08);
  cursor: not-allowed;
}

/* Buttons */
.smc-btn {
  border: 1px solid var(--smc-btn-border);
  background: var(--smc-btn-bg);
  color: var(--smc-btn-fg) !important;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
}
.smc-btn:hover {
  background: rgba(148, 163, 184, 0.08);
}
.smc-btn.primary {
  background: var(--smc-btn-primary-bg);
  color: var(--smc-btn-primary-fg) !important;
  border-color: var(--smc-btn-primary-border);
}
.smc-btn.ghost {
  background: transparent;
}
.smc-btn.danger {
  background: #ef4444;
  border-color: #dc2626;
  color: #fff !important;
}
.smc-btn.danger.ghost {
  background: transparent;
  color: #ef4444 !important;
  border-color: #fecaca;
}
.smc-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  filter: grayscale(10%);
}

/* Link */
.smc-link {
  color: var(--smc-link);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

/* Transition */
.smc-fade-enter-active,
.smc-fade-leave-active {
  transition: opacity 0.12s ease;
}
.smc-fade-enter-from,
.smc-fade-leave-to {
  opacity: 0;
}

/* Sessions */
.smc-sessions {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.smc-session-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-bottom: 1px solid var(--smc-border);
  background: var(--smc-bg);
  color: var(--smc-text);
}
.smc-spacer {
  flex: 1;
}
.smc-pager {
  display: flex;
  align-items: center;
  gap: 8px;
}
.smc-page {
  font-size: 12px;
  color: var(--smc-text);
  opacity: 0.7;
}
.smc-session-list {
  overflow-y: visible;
  padding: 8px;
  background: transparent;
}
.smc-session-row {
  position: relative;
  display: flex;
  align-items: center;
}
.smc-session-item {
  border: 1px solid var(--smc-border);
  background: var(--smc-bg);
  color: var(--smc-text);
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  position: relative;
  z-index: 1;
}
.smc-session-item.open {
  z-index: 100;
}
.smc-session-item.active {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}
.smc-session-title {
  font-weight: 600;
  margin-bottom: 4px;
}
.smc-session-meta {
  font-size: 12px;
  color: var(--smc-text);
  opacity: 0.7;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.smc-session-main {
  flex: 1;
  min-width: 0;
}

/* Kebab & menu */
.smc-kebab {
  border: 1px solid var(--smc-btn-border);
  background: var(--smc-btn-bg);
  color: var(--smc-btn-fg) !important;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.smc-kebab:hover {
  background: rgba(148, 163, 184, 0.08);
}
.smc-menu {
  position: absolute;
  top: 100%;
  right: 8px;
  margin-top: -20px;
  min-width: 140px;
  background: var(--smc-bg);
  border: 1px solid var(--smc-border);
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  padding: 6px;
  z-index: 1000;
}
.smc-menu-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: var(--smc-text);
}
.smc-menu-item:hover {
  background: rgba(148, 163, 184, 0.08);
}
.smc-menu-item.danger {
  color: #dc2626;
}
.smc-menu-item.danger:hover {
  background: rgba(220, 38, 38, 0.1);
}
.smc-stop-btn {
  border-radius: 7px;
  width: 25px;
  height: 25px;
  padding: 0;
  margin-left: 10px;
}
</style>
